import { defineComponent, onBeforeMount, ref } from 'vue';
import { useAuthStore } from '@/store/modules/auth';
import { useKycStore } from '@/store/modules/kyc';
import { storeToRefs } from 'pinia';
import AccessError from '@/components/general/AccessError.vue';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import useTransactionsStore from '@/store/modules/transactions';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-vue-v3';
import { useRoute } from 'vue-router';
import router, { RoutePaths } from '@/router';
import { TransactTypes } from '@/types/transact';
import { logoutUserAndPartner } from '@/utils/logout';
import setContext from '@/utils/ddrum';
import { getFallbackDeviceId } from '@/utils/deviceId';
import { resolveLandingFlow } from '@/utils/flow';
import { Widget } from '@/types/widget';
export default defineComponent({
    name: 'LandingView',
    props: ['apiKey'],
    components: {
        AccessError,
        LoaderComponent,
    },
    setup() {
        const authStore = useAuthStore();
        const { partnerVerified, deviceId, partnerKey, isLoggedIn, partnerId } = storeToRefs(authStore);
        const transactionsStore = useTransactionsStore();
        const { loadingMessage, currentStep, loadingError } = storeToRefs(transactionsStore);
        const kycStore = useKycStore();
        const fatalError = ref();
        const loading = ref(true);
        loadingMessage.value = 'Verifying';
        const route = useRoute();
        const { query } = route;
        let data;
        let getData;
        const getUserDeviceData = async () => {
            try {
                const { data: fpjsData, getData: fpjsGetData } = useVisitorData({ extendedResult: true }, { immediate: false });
                data = fpjsData;
                getData = fpjsGetData;
            }
            catch (e) {
                console.error('failed to get user device data:', e);
            }
        };
        // Alternative no-login entry flows skip the buy/sell setup entirely and
        // land on their own read-only view. Returns whether it routed the user,
        // and throws when the flow params are invalid.
        const pushFlowRoute = async () => {
            const resolvedFlow = resolveLandingFlow(query);
            if (resolvedFlow?.flow !== Widget.Flow.FIAT_SETTLEMENT)
                return false;
            await router.push({
                path: RoutePaths.Authorize,
                query: { txId: resolvedFlow.txId },
            });
            return true;
        };
        const verifyPartnerDetails = async (apiKey) => {
            try {
                await getUserDeviceData();
                await getData();
            }
            catch (e) {
                console.warn('fpjs failed, using fallback:', e);
                deviceId.value = getFallbackDeviceId();
            }
            try {
                deviceId.value = data?.value?.visitorId;
                loading.value = true;
                // if the wallet address is passed as query param and the signature
                // is not passed, or the crypto is editable (ie. the token was not passed)
                // then we error
                if (loadingError.value) {
                    fatalError.value = true;
                    loading.value = false;
                }
                else {
                    await authStore.VerifyApiKey({
                        apiKey: apiKey,
                        redirectLink: query.redirectLink?.toString(),
                        redirectLinkSignature: query.redirectLinkSignature?.toString(),
                    });
                    // set partner id in dd rum
                    setContext('partnerId', partnerId.value);
                    fatalError.value = false;
                    loading.value = false;
                    if (await pushFlowRoute())
                        return;
                    currentStep.value = TransactTypes.TransactSteps.BUY_1;
                    await transactionsStore.getRates();
                    transactionsStore.setFromQueryParams(query);
                    authStore.setFromQueryParams(query);
                    kycStore.setFromQueryParams(query);
                    await router.push(RoutePaths.Transact);
                }
            }
            catch (e) {
                partnerVerified.value = false;
                loading.value = false;
                fatalError.value = true;
                console.error('failed to verify partner api key:', e);
            }
        };
        onBeforeMount(async () => {
            const key = route.params.apiKey;
            if (!partnerVerified.value) {
                if (!key) {
                    fatalError.value = true;
                    loading.value = false;
                }
                else {
                    await verifyPartnerDetails(key);
                }
            }
            else {
                // if different apikey to one currently stored, then restart verification with new partner
                if (key && key !== partnerKey.value) {
                    if (isLoggedIn.value)
                        await authStore.Logout(true);
                    logoutUserAndPartner();
                    await verifyPartnerDetails(key);
                    return;
                }
                fatalError.value = false;
                loading.value = false;
                // an already verified partner reloading a flow url (eg. an iframe
                // refresh) must land back on the flow view, not on home
                try {
                    if (await pushFlowRoute())
                        return;
                }
                catch (e) {
                    fatalError.value = true;
                    console.error('failed to resolve landing flow:', e);
                    return;
                }
                await router.push('/');
            }
        });
        return {
            partnerVerified,
            fatalError,
            loading,
        };
    },
});
